/**
 * slider.js
 * Bridges external slider buttons (placed outside <slider-component>)
 * to the correct slider scroll behaviour.
 *
 * How it works:
 *  - Listens for clicks on any prev/next button that has [aria-controls]
 *    pointing to a Slider-* element.
 *  - If the button is INSIDE a <slider-component>, it skips it —
 *    global.js already handles those natively.
 *  - If the button is OUTSIDE (e.g. desktop buttons in the title bar),
 *    it calculates the scroll position and scrolls the slider.
 *  - After scrolling it updates the disabled state of ALL external buttons
 *    targeting that same slider.
 */

(function () {
  // ─── Click delegation ──────────────────────────────────────────────────────
  document.addEventListener('click', function (event) {
    const button = event.target.closest('button[name="previous"], button[name="next"]');
    if (!button) return;

    const sliderId = button.getAttribute('aria-controls');
    if (!sliderId) return;

    const slider = document.getElementById(sliderId);
    if (!slider) return;

    // If the button lives inside a slider-component, global.js handles it.
    if (button.closest('slider-component')) return;

    event.preventDefault();

    // Calculate item offset the same way SliderComponent does in global.js
    const sliderItems = Array.from(
      slider.querySelectorAll('[id^="Slide-"]')
    ).filter((el) => el.clientWidth > 0);

    if (sliderItems.length < 2) return;

    const itemOffset = sliderItems[1].offsetLeft - sliderItems[0].offsetLeft;
    const step = parseInt(button.dataset.step, 10) || 1;

    const newPosition =
      button.name === 'next'
        ? slider.scrollLeft + step * itemOffset
        : slider.scrollLeft - step * itemOffset;

    slider.scrollTo({ left: newPosition });

    // After the native scroll-snap settles, update disabled state
    setTimeout(() => syncExternalButtons(sliderId, slider), 350);
  });

  // ─── Scroll listener — keep external buttons in sync while user scrolls ───
  document.addEventListener(
    'scroll',
    function (event) {
      const slider = event.target;
      if (!slider || !slider.id || !slider.id.startsWith('Slider-')) return;
      syncExternalButtons(slider.id, slider);
    },
    true // capture phase so we receive scroll events from the slider <ul>
  );

  // ─── Helpers ───────────────────────────────────────────────────────────────
  function syncExternalButtons(sliderId, slider) {
    const externalPrev = Array.from(
      document.querySelectorAll(
        `button[name="previous"][aria-controls="${sliderId}"]`
      )
    ).filter((btn) => !btn.closest('slider-component'));

    const externalNext = Array.from(
      document.querySelectorAll(
        `button[name="next"][aria-controls="${sliderId}"]`
      )
    ).filter((btn) => !btn.closest('slider-component'));

    // At the very start → disable prev
    if (slider.scrollLeft <= 5) {
      externalPrev.forEach((btn) => btn.setAttribute('disabled', 'disabled'));
    } else {
      externalPrev.forEach((btn) => btn.removeAttribute('disabled'));
    }

    // At the very end → disable next
    const atEnd =
      slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 5;
    if (atEnd) {
      externalNext.forEach((btn) => btn.setAttribute('disabled', 'disabled'));
    } else {
      externalNext.forEach((btn) => btn.removeAttribute('disabled'));
    }
  }

  // ─── Initial state on page load ────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[id^="Slider-"]').forEach((slider) => {
      syncExternalButtons(slider.id, slider);
    });
  });
})();
